import React, { ReactNode } from 'react';
import styled from 'styled-components';
import FilterInfo from './FilterInfo';
import { IEventConditionFieldsFragment } from '../../../utils/apollo/graphqlSchema';
import * as BuilderInfoUI from '../BuilderInfoUI';
import { notEqualSign } from '../../../utils/charCodeSigns';
import { level1Content } from '../../../styles/shareableStyles';
import BuilderInfoLabelList from './BuilderInfoLabelList';

interface IProps {
  condition: IEventConditionFieldsFragment;
  label: ReactNode;
  eventNameFormatter?: (value: string) => string;
  showProperty?: boolean;
  noMarginBottom?: boolean;
  isNegativeStep?: boolean;
}

const Container = styled.div<{ $noMarginBottom?: boolean }>`
  ${({ $noMarginBottom }) =>
    !$noMarginBottom &&
    `
     &:not(:last-of-type) {
      margin-bottom: 4px;
     }
  `}
`;

const EventWrapper = styled.div`
  width: 100%;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  flex-wrap: wrap;
  ${level1Content};
`;

const FilterWrapper = styled.div`
  padding: 6px 8px;
`;

const ExcludeIcon = styled.div`
  margin-right: 4px;
  font-weight: 500;
  line-height: 1;
  margin-bottom: 2px;
`;

const EventsWrapper = styled(BuilderInfoUI.BorderedInfo)`
  min-width: 120px;
  max-width: 240px;
  margin-right: 0;
`;

const EventConditionInfo = ({
  condition,
  label,
  showProperty,
  noMarginBottom,
  isNegativeStep,
  eventNameFormatter,
}: IProps) => {
  return (
    <Container $noMarginBottom={noMarginBottom}>
      <EventWrapper>
        <BuilderInfoUI.PlainInfo>
          {isNegativeStep && <ExcludeIcon>{notEqualSign}</ExcludeIcon>}
          <BuilderInfoUI.InfoLabel>{label}</BuilderInfoUI.InfoLabel>
          <EventsWrapper>
            {condition.events.map((event) => {
              if (
                event.__typename === 'AnyEvent' ||
                event.__typename === 'AnyTableEvent'
              ) {
                return (
                  <BuilderInfoUI.StyledPopupLabel key={event.name}>
                    {event.name}
                  </BuilderInfoUI.StyledPopupLabel>
                );
              }

              if (event.__typename === 'VirtualEvent') {
                return (
                  <BuilderInfoUI.VersionedEntityPopupLabel
                    key={event.name}
                    $isDisabled={event.isDisabled}
                    $isLatestVersion={event.isLatestVersion}
                  >
                    {event.name}
                  </BuilderInfoUI.VersionedEntityPopupLabel>
                );
              }

              const label = eventNameFormatter
                ? eventNameFormatter(event.value.name)
                : event.value.name;

              return (
                <BuilderInfoUI.StyledPopupLabel
                  key={label}
                  $isDisabled={event.value.isDisabled}
                >
                  {label}
                </BuilderInfoUI.StyledPopupLabel>
              );
            })}
          </EventsWrapper>
        </BuilderInfoUI.PlainInfo>
        {condition.field && showProperty && (
          <BuilderInfoUI.PlainInfo>
            <BuilderInfoUI.InfoLabel>Property</BuilderInfoUI.InfoLabel>
            <BuilderInfoLabelList items={[condition.field.name]} />
          </BuilderInfoUI.PlainInfo>
        )}
      </EventWrapper>
      {condition.filter && (
        <FilterWrapper>
          <FilterInfo filter={condition.filter} />
        </FilterWrapper>
      )}
    </Container>
  );
};

export default EventConditionInfo;
